/**
 * UnitRenderer.ts
 * Draws all units as coloured circle shapes on the iso grid.
 * Each unit gets one Graphics object; shapes are redrawn when state changes
 * (selection, HP) and depth-sorted by (col + row) every frame for correct
 * painter's-order occlusion.
 *
 * Sprites replace these shapes in a later stage — the interface stays the same.
 */

import { Container, Graphics } from 'pixi.js';
import type { Unit } from '../entities/Unit';
import type { TileMap } from '../map/TileMap';
import type { BandClass } from '../types';
import { Faction, FogState } from '../types';
import {
  ATTACK_PULSE_DURATION,
  SONG_DROP_STUN_SECS,
  UNIT_RADIUS_RECRUIT,
  UNIT_RADIUS_HERO,
  UNIT_RADIUS_FAN,
  UNIT_RADIUS_MOGUL,
  UNIT_RADIUS_SWARM,
  UNIT_RADIUS_BAD_MANAGER,
  UNIT_RADIUS_TRIBUTE,
  UNIT_RADIUS_MUSIC_PRESS,
  UNIT_RADIUS_PIRATE,
} from '../constants';

// ── Colours ───────────────────────────────────────────────────────────────
/** Fill colour per band class — matches the game design doc. */
export const CLASS_COLOUR: Record<BandClass, number> = {
  protector:  0x4488ff, // blue   — Steve & The Guitar Gang
  promoter:   0xff44aa, // pink   — Ali & The Vocal Mob
  engine:     0xff8833, // orange — Chris & The Rhythm Crew
  sustain:    0x44cc66, // green  — Alex & The Bass Line
  mystic:     0xaa44ff, // purple — Jim & The Keyboard Prophets
  songwriter: 0xe8c84a, // warm gold — Nigel & The Song Catchers
  source:     0xffd700, // gold   — Nigel (inside the Beacon, non-deployable)
  fan:        0x22ddcc, // cyan   — venue fans; matches FAN resource colour
};
const ENEMY_COLOUR  = 0xff3333;
const MOGUL_COLOUR  = 0xb7263f;
const SWARM_COLOUR  = 0xff4a3d;
const BAD_MANAGER_COLOUR = 0x7f2a8d;
const TRIBUTE_COLOUR = 0x5570d8;
const MUSIC_PRESS_COLOUR = 0xb8bac0;
const PIRATE_COLOUR = 0x20c7d8;
const SELECT_COLOUR = 0x00ffff;
const HERO_RING     = 0xffd700;
const ATTACK_PULSE_COLOUR = 0xffa02b;
const DOWNED_COLOUR = 0x777777;
const REVIVE_COLOUR = 0x44ff99;
const STUN_COLOUR   = 0x39d9ff;
const BADDIE_BEACON_COLOUR = 0xff245d;
const BADDIE_BEACON_CORE   = 0xffd36a;
const MONOLITH_COLOUR      = 0x6f1027;
const MONOLITH_CORE        = 0xff3b3b;
const SIGNAL_JAMMER_COLOUR = 0xff2fcf;
const REPAIR_CREW_COLOUR   = 0xff8a2a;
const CORPORATE_HQ_COLOUR  = 0x202431;
const CORPORATE_HQ_CORE    = 0xff3355;
const RIVAL_BUILDER_COLOUR = 0xd44a9a;
const RIVAL_VENUE_COLOUR   = 0x3b1f36;
const RIVAL_VENUE_CORE     = 0xffb347;
const FAN_DEATH_COLOUR = 0xffd36a;
const DEATH_LIFETIME = 0.45;

// ── HP bar ────────────────────────────────────────────────────────────────
const HP_W = 24;
const HP_H = 3;

interface DeathAnim {
  g: Graphics;
  elapsed: number;
  radius: number;
  colour: number;
  isFan: boolean;
  isEnemy: boolean;
}

export class UnitRenderer {
  private readonly container = new Container();
  /** Graphics object keyed by unit id. */
  private readonly gfx = new Map<string, Graphics>();
  /** Snapshot of each unit's last drawn state to skip redundant redraws. */
  private readonly stateCache = new Map<string, string>();
  /** Short-lived graphics for units that have just been removed from play. */
  private readonly deathAnims: DeathAnim[] = [];

  /**
   * Optional fog-state callback wired up by Game.ts once FogSystem is ready.
   * Enemy units on non-Visible tiles are hidden from the renderer.
   */
  private fogCheck: ((col: number, row: number) => FogState) | null = null;

  constructor(world: Container, private readonly map: TileMap) {
    world.addChild(this.container);
  }

  /** Connect the fog-of-war system. Call once in Game.start() after FogSystem exists. */
  setFogCheck(fn: (col: number, row: number) => FogState): void {
    this.fogCheck = fn;
  }

  /** Register a new unit — creates its display object. */
  add(unit: Unit): void {
    if (this.gfx.has(unit.id)) return;
    const g = new Graphics();
    this.container.addChild(g);
    this.gfx.set(unit.id, g);
    this.drawUnit(g, unit, 0);
  }

  /** Unregister a unit and destroy its display object. */
  remove(unit: Unit): void {
    const g = this.gfx.get(unit.id);
    if (!g) return;
    this.gfx.delete(unit.id);
    this.stateCache.delete(unit.id);

    // Fans and enemies actually die, so leave a tiny visual beat behind before
    // their marker disappears. Downed band members stay alive and are redrawn.
    this.deathAnims.push({
      g,
      elapsed: 0,
      radius: this.radiusFor(unit),
      colour: unit.bandClass === 'fan' ? FAN_DEATH_COLOUR : this.colourFor(unit),
      isFan: unit.bandClass === 'fan',
      isEnemy: unit.enemyType !== null,
    });
  }

  /**
   * Update positions, redraw changed units, and depth-sort the layer.
   * Call once per render frame.
   */
  render(units: Unit[], elapsed: number): void {
    for (const unit of units) {
      if (!unit.alive) { this.remove(unit); continue; }

      const g = this.gfx.get(unit.id);
      if (!g) continue;

      // Fog of war — non-player units are hidden when outside a friendly sight radius.
      // Player units are always visible so they're never obscured by the overlay.
      if (unit.faction !== Faction.Player && this.fogCheck) {
        const fogState = this.fogCheck(unit.gridPos.col, unit.gridPos.row);
        g.visible = fogState === FogState.Visible;
        if (!g.visible) continue;
      } else {
        g.visible = true;
      }

      // Smooth position — lerp between current tile and next when moving
      const curr = this.map.tileTopCenter(unit.gridPos.col, unit.gridPos.row);
      let wx = curr.x, wy = curr.y;
      if (unit.path.length > 0 && unit.moveT > 0) {
        const next = this.map.tileTopCenter(unit.path[0].col, unit.path[0].row);
        wx = curr.x + (next.x - curr.x) * unit.moveT;
        wy = curr.y + (next.y - curr.y) * unit.moveT;
      }
      g.position.set(wx, wy);

      // Hit-flash tint — cheap property change, no redraw needed
      g.tint = unit.hitFlash > 0 ? 0xff8888 : 0xffffff;

      // Redraw only when visual state changed
      const pulseFrame = unit.attackPulse > 0 ? Math.ceil(unit.attackPulse * 100) : 0;
      const reviveFrame = unit.reviveProgress > 0 ? Math.ceil(unit.reviveProgress * 100) : 0;
      const downedBeat = unit.isDowned ? Math.floor(elapsed * 5) : 0;
      const stunFrame = unit.stunTimer > 0 ? Math.ceil(unit.stunTimer * 20) : 0;
      const buildFrame = unit.constructionProgress > 0 ? Math.ceil(unit.constructionProgress * 100) : 0;
      const monolithFrame = unit.specialRole === 'streamingMonolith' ? Math.floor(elapsed * 8) : 0;
      const speedBoost  = unit.speedBoostTimer  > 0 ? 1 : 0;
      const attackBoost = unit.attackBoostTimer > 0 ? 1 : 0;
      const key = `${unit.selected}|${unit.hp}|${unit.isHero}|${unit.enemyType}|${unit.faction}|${unit.displayName}|${unit.specialRole}|${buildFrame}|${unit.constructionComplete}|${pulseFrame}|${unit.isMining}|${unit.isDowned}|${reviveFrame}|${downedBeat}|${stunFrame}|${monolithFrame}|${speedBoost}|${attackBoost}`;
      if (this.stateCache.get(unit.id) !== key) {
        this.drawUnit(g, unit, elapsed);
        this.stateCache.set(unit.id, key);
      }
    }

    this.renderDeathAnims();

    // Depth sort: painter's order — use next tile when moving for correct occlusion
    const sorted = units
      .filter(u => u.alive && this.gfx.has(u.id))
      .sort((a, b) => {
        const ac = a.path.length > 0 ? a.path[0] : a.gridPos;
        const bc = b.path.length > 0 ? b.path[0] : b.gridPos;
        return (ac.col + ac.row) - (bc.col + bc.row);
      });

    sorted.forEach((u, i) => {
      const g = this.gfx.get(u.id);
      if (g) this.container.setChildIndex(g, Math.min(i, this.container.children.length - 1));
    });
  }

  // ── Drawing ─────────────────────────────────────────────────────────────

  private drawUnit(g: Graphics, unit: Unit, elapsed: number): void {
    const radius  = this.radiusFor(unit);
    const colour  = unit.isDowned ? DOWNED_COLOUR : this.colourFor(unit);
    const hpPct   = unit.hp / unit.maxHp;
    const downedBeat = unit.isDowned ? (Math.sin(elapsed * Math.PI * 2.2) + 1) / 2 : 0;

    g.clear();

    // Ground shadow (subtle ellipse beneath the unit)
    g.beginFill(0x000000, 0.2);
    g.drawEllipse(2, radius * 0.6, radius * 1.1, radius * 0.45);
    g.endFill();

    // Selection ring
    if (unit.selected) {
      g.lineStyle(2, SELECT_COLOUR, 0.9);
      g.drawCircle(0, 0, radius + 6);
      g.lineStyle(0);
    }

    // Hero gold ring
    if (unit.isHero) {
      g.lineStyle(2.5, unit.isDowned ? DOWNED_COLOUR : HERO_RING, unit.isDowned ? 0.55 : 0.75);
      g.drawCircle(0, 0, radius + (unit.selected ? 11 : 3));
      g.lineStyle(0);
    }

    // Streaming Monolith — siege-objective warning halo. It needs to read as
    // a boss-piece from a distance, not a regular red triangle.
    if (unit.specialRole === 'streamingMonolith' && !unit.isDowned) {
      const beat = (Math.sin(elapsed * Math.PI * 2.4) + 1) / 2;
      const halo = radius + 8 + beat * 6;
      g.lineStyle(3, MONOLITH_CORE, 0.35 + beat * 0.3);
      g.drawCircle(0, 0, halo);
      g.lineStyle(1.5, BADDIE_BEACON_CORE, 0.35);
      g.drawCircle(0, 0, radius + 3);
      g.lineStyle(0);
    }

    if (unit.specialRole === 'corporateHQ' && !unit.isDowned) {
      const beat = (Math.sin(elapsed * Math.PI * 1.8) + 1) / 2;
      g.lineStyle(3, CORPORATE_HQ_CORE, 0.25 + beat * 0.25);
      g.drawRect(-radius - 8, -radius - 10, (radius + 8) * 2, (radius + 12) * 2);
      g.lineStyle(1.5, 0xffffff, 0.16);
      g.drawRect(-radius - 2, -radius - 4, (radius + 2) * 2, (radius + 5) * 2);
      g.lineStyle(0);
    }

    if (unit.specialRole === 'rivalVenue' && !unit.isDowned) {
      const beat = (Math.sin(elapsed * Math.PI * 2.1) + 1) / 2;
      g.lineStyle(2.5, RIVAL_VENUE_CORE, unit.constructionComplete ? 0.28 + beat * 0.22 : 0.5);
      g.drawRect(-radius - 5, -radius - 7, (radius + 5) * 2, (radius + 8) * 2);
      g.lineStyle(0);
    }

    // Crowd Hype speed boost — gold outer ring while Ali's buff is active
    if (unit.speedBoostTimer > 0 && !unit.isDowned) {
      g.lineStyle(2.5, 0xffd700, 0.60);
      g.drawCircle(0, 0, radius + (unit.selected ? 18 : 13));
      g.lineStyle(0);
    }

    // Tempo Surge attack boost — hot-orange outer ring while Chris's buff is active
    if (unit.attackBoostTimer > 0 && !unit.isDowned) {
      g.lineStyle(2.5, 0xff6a00, 0.60);
      g.drawCircle(0, 0, radius + (unit.selected ? 23 : 18));
      g.lineStyle(0);
    }

    // Downed band members need help from another band member.
    if (unit.isDowned) {
      const pulseRadius = radius + 8 + downedBeat * 4;
      g.lineStyle(2, REVIVE_COLOUR, 0.75 - downedBeat * 0.35);
      g.drawCircle(0, 0, pulseRadius);
      g.lineStyle(1.5, 0xffffff, 0.2);
      g.drawCircle(0, 0, radius + 3);
      g.moveTo(-radius * 0.7, 0);
      g.lineTo(radius * 0.7, 0);
      g.moveTo(0, -radius * 0.7);
      g.lineTo(0, radius * 0.7);
      g.lineStyle(0);
    }

    // Mining ring — small gold halo above fans that are extracting GEAR
    if (unit.isMining) {
      g.lineStyle(1.5, 0xffd700, 0.85);
      g.drawCircle(0, -(radius + 5), radius * 0.7);
      g.lineStyle(0);
    }

    // Attack pulse — fast orange diamond around the unit as it swings
    if (unit.attackPulse > 0) {
      const remaining = unit.attackPulse / ATTACK_PULSE_DURATION;
      const t = 1 - remaining;
      const alpha = Math.max(0, remaining) * 0.95;
      const x = (radius + 14) * (0.85 + t * 0.75);
      const y = (radius + 7) * (0.85 + t * 0.75);
      g.lineStyle(2, ATTACK_PULSE_COLOUR, alpha);
      g.moveTo(0, -y);
      g.lineTo(x, 0);
      g.lineTo(0, y);
      g.lineTo(-x, 0);
      g.closePath();
      g.lineStyle(0);
    }

    // Stunned/frozen units stay upright but read as temporarily locked.
    if (!unit.isDowned && unit.stunTimer > 0) {
      const lockT = Math.max(0, Math.min(1, unit.stunTimer / SONG_DROP_STUN_SECS));
      const flicker = 0.65 + Math.sin(elapsed * 18) * 0.18;
      const x = radius + 10;
      const y = radius + 5;
      g.lineStyle(2, STUN_COLOUR, flicker);
      g.moveTo(0, -y);
      g.lineTo(x, 0);
      g.lineTo(0, y);
      g.lineTo(-x, 0);
      g.closePath();
      g.lineStyle(1, 0xb7f5ff, 0.8);
      g.moveTo(-x * 0.45, -y * 0.45);
      g.lineTo(x * 0.45, y * 0.45);
      g.moveTo(x * 0.45, -y * 0.45);
      g.lineTo(-x * 0.45, y * 0.45);
      g.lineStyle(0);

      // Small draining status tick above the unit.
      g.beginFill(0x062a36, 0.85);
      g.drawRect(-HP_W / 2, -(radius + 15), HP_W, 2);
      g.endFill();
      g.beginFill(STUN_COLOUR, 0.95);
      g.drawRect(-HP_W / 2, -(radius + 15), HP_W * lockT, 2);
      g.endFill();
    }

    // Body fill + thin dark border. Opposition pieces use triangles so they
    // read differently from player circles even when colours are obscured.
    g.lineStyle(1, 0x000000, 0.35);
    g.beginFill(colour);
    if (unit.isDowned) {
      // Collapsed marker: grey, tilted, and low, distinct from frozen/stunned.
      g.drawPolygon([
        -radius * 0.95, -radius * 0.2,
        radius * 0.55, -radius * 0.55,
        radius * 0.95, radius * 0.2,
        -radius * 0.55, radius * 0.55,
      ]);
    } else if (unit.specialRole === 'streamingMonolith') {
      const mastH = radius * 1.35;
      g.drawPolygon([
        0, -mastH,
        radius * 0.95, -radius * 0.15,
        radius * 0.55, radius * 0.95,
        0, radius * 0.62,
        -radius * 0.55, radius * 0.95,
        -radius * 0.95, -radius * 0.15,
      ]);
      g.endFill();

      g.beginFill(0x1a0610, 0.92);
      g.drawPolygon([
        0, -mastH * 0.72,
        radius * 0.42, -radius * 0.05,
        radius * 0.2, radius * 0.46,
        0, radius * 0.32,
        -radius * 0.2, radius * 0.46,
        -radius * 0.42, -radius * 0.05,
      ]);
      g.endFill();

      g.beginFill(MONOLITH_CORE, 0.96);
      g.drawCircle(0, -radius * 0.2, radius * 0.22);
      g.endFill();

      g.lineStyle(2, BADDIE_BEACON_CORE, 0.75);
      g.moveTo(0, -mastH);
      g.lineTo(0, -mastH - radius * 0.32);
      g.moveTo(-radius * 0.38, -mastH * 0.74);
      g.lineTo(-radius * 0.68, -mastH * 0.92);
      g.moveTo(radius * 0.38, -mastH * 0.74);
      g.lineTo(radius * 0.68, -mastH * 0.92);
      g.lineStyle(0);
    } else if (unit.specialRole === 'corporateHQ') {
      const w = radius * 1.45;
      const h = radius * 1.7;
      g.drawRect(-w * 0.5, -h * 0.55, w, h);
      g.endFill();
      g.beginFill(CORPORATE_HQ_CORE, 0.88);
      for (let i = 0; i < 3; i++) {
        g.drawRect(-w * 0.32 + i * w * 0.28, -h * 0.32, w * 0.14, h * 0.55);
      }
      g.endFill();
      g.beginFill(0x0a0c10, 0.9);
      g.drawRect(-w * 0.62, h * 0.18, w * 1.24, h * 0.16);
    } else if (unit.specialRole === 'rivalVenue') {
      const w = radius * 1.45;
      const h = radius * 1.2;
      g.drawRect(-w * 0.5, -h * 0.45, w, h);
      g.endFill();
      g.beginFill(RIVAL_VENUE_CORE, unit.constructionComplete ? 0.9 : 0.55);
      g.drawPolygon([
        -w * 0.6, -h * 0.45,
        0, -h * 0.92,
        w * 0.6, -h * 0.45,
      ]);
      g.endFill();
      g.beginFill(0x09070a, 0.9);
      g.drawRect(-w * 0.16, h * 0.04, w * 0.32, h * 0.42);
      g.drawRect(-w * 0.38, -h * 0.24, w * 0.18, h * 0.18);
      g.drawRect(w * 0.2, -h * 0.24, w * 0.18, h * 0.18);
    } else if (unit.specialRole === 'baddieBeacon') {
      const mastH = radius * 1.35;
      g.drawPolygon([
        0, -mastH,
        radius * 0.8, 0,
        0, radius * 0.75,
        -radius * 0.8, 0,
      ]);
      g.endFill();
      g.beginFill(BADDIE_BEACON_CORE, unit.constructionComplete ? 0.95 : 0.65);
      g.drawCircle(0, -radius * 0.1, radius * 0.35);
    } else if (unit.faction === Faction.Player) {
      g.drawCircle(0, 0, radius);
    } else {
      g.drawPolygon([
        0, -radius,
        radius * 0.95, radius * 0.82,
        -radius * 0.95, radius * 0.82,
      ]);
    }
    g.endFill();
    g.lineStyle(0);

    // HP bar — only when damaged
    if (hpPct < 1) {
      const barY  = -(radius + 9);
      const barX  = -HP_W / 2;
      const fillPct = unit.isDowned ? unit.reviveProgress : hpPct;
      const hpCol = unit.isDowned ? REVIVE_COLOUR : hpPct > 0.5 ? 0x44cc66 : hpPct > 0.25 ? 0xffaa00 : 0xff3333;
      g.beginFill(0x1a1a1a, 0.85);
      g.drawRect(barX, barY, HP_W, HP_H);
      g.endFill();
      g.beginFill(hpCol);
      g.drawRect(barX, barY, HP_W * fillPct, HP_H);
      g.endFill();
    }

    if ((unit.specialRole === 'baddieBeacon' || unit.specialRole === 'rivalVenue') && !unit.constructionComplete) {
      const barY = radius + 7;
      const barW = HP_W * 1.2;
      g.beginFill(0x1a1a1a, 0.9);
      g.drawRect(-barW / 2, barY, barW, HP_H + 1);
      g.endFill();
      g.beginFill(unit.specialRole === 'rivalVenue' ? RIVAL_VENUE_CORE : BADDIE_BEACON_CORE, 0.95);
      g.drawRect(-barW / 2, barY, barW * unit.constructionProgress, HP_H + 1);
      g.endFill();
    }
  }

  private renderDeathAnims(): void {
    for (let i = this.deathAnims.length - 1; i >= 0; i--) {
      const anim = this.deathAnims[i];
      anim.elapsed += 1 / 60;
      const t = Math.min(1, anim.elapsed / DEATH_LIFETIME);
      this.drawDeathAnim(anim, t);

      if (t >= 1) {
        this.container.removeChild(anim.g);
        anim.g.destroy();
        this.deathAnims.splice(i, 1);
      }
    }
  }

  private drawDeathAnim(anim: DeathAnim, t: number): void {
    const g = anim.g;
    const alpha = 1 - t;
    const r = anim.radius * (1 - t * 0.65);
    g.clear();

    if (anim.isFan) {
      g.beginFill(FAN_DEATH_COLOUR, 0.7 * alpha);
      g.drawCircle(0, 0, Math.max(1, r));
      g.endFill();
      for (let i = 0; i < 5; i++) {
        const a = (Math.PI * 2 * i) / 5;
        const d = 5 + t * 18;
        g.beginFill(i % 2 === 0 ? 0xffd700 : 0x44ff99, alpha);
        g.drawCircle(Math.cos(a) * d, Math.sin(a) * d * 0.65, 1.5 + (1 - t));
        g.endFill();
      }
      return;
    }

    const shardColour = anim.isEnemy ? 0xff5a45 : anim.colour;
    g.lineStyle(2, shardColour, 0.85 * alpha);
    g.drawCircle(0, 0, anim.radius + t * 12);
    g.lineStyle(0);
    for (let i = 0; i < 4; i++) {
      const a = (Math.PI * 2 * i) / 4 + Math.PI / 4;
      const d = 4 + t * 15;
      g.beginFill(shardColour, 0.75 * alpha);
      g.drawPolygon([
        Math.cos(a) * d, Math.sin(a) * d,
        Math.cos(a + 0.35) * (d + 5), Math.sin(a + 0.35) * (d + 5),
        Math.cos(a - 0.35) * (d + 5), Math.sin(a - 0.35) * (d + 5),
      ]);
      g.endFill();
    }
  }

  private radiusFor(unit: Unit): number {
    const scale = Math.max(0.75, unit.threatScale || 1);
    if (unit.bandClass === 'fan') return UNIT_RADIUS_FAN * scale;
    if (unit.isHero) return UNIT_RADIUS_HERO * scale;
    if (unit.enemyType) {
      if (unit.specialRole === 'baddieBeacon') return UNIT_RADIUS_MOGUL * scale;
      if (unit.specialRole === 'corporateHQ') return UNIT_RADIUS_MOGUL * scale;
      if (unit.specialRole === 'rivalVenue') return UNIT_RADIUS_MOGUL * scale;
      if (unit.specialRole === 'rivalVenueBuilder') return UNIT_RADIUS_BAD_MANAGER * scale;
      if (unit.enemyType === 'StreamingMogul') return UNIT_RADIUS_MOGUL * scale;
      if (unit.enemyType === 'GenZSwarm') return UNIT_RADIUS_SWARM * scale;
      if (unit.enemyType === 'BadManager') return UNIT_RADIUS_BAD_MANAGER * scale;
      if (unit.enemyType === 'TributeAct') return UNIT_RADIUS_TRIBUTE * scale;
      if (unit.enemyType === 'MusicPress') return UNIT_RADIUS_MUSIC_PRESS * scale;
      if (unit.enemyType === 'FilePirate') return UNIT_RADIUS_PIRATE * scale;
    }
    return UNIT_RADIUS_RECRUIT * scale;
  }

  private colourFor(unit: Unit): number {
    if (unit.bandClass) return CLASS_COLOUR[unit.bandClass];
    if (unit.specialRole === 'corporateHQ') return CORPORATE_HQ_COLOUR;
    if (unit.specialRole === 'rivalVenue') return RIVAL_VENUE_COLOUR;
    if (unit.specialRole === 'rivalVenueBuilder') return RIVAL_BUILDER_COLOUR;
    if (unit.specialRole === 'baddieBeacon') return BADDIE_BEACON_COLOUR;
    if (unit.specialRole === 'streamingMonolith') return MONOLITH_COLOUR;
    if (unit.displayName === 'Signal Jammer') return SIGNAL_JAMMER_COLOUR;
    if (unit.displayName === 'Repair Crew') return REPAIR_CREW_COLOUR;
    if (unit.enemyType === 'StreamingMogul') return MOGUL_COLOUR;
    if (unit.enemyType === 'GenZSwarm') return SWARM_COLOUR;
    if (unit.enemyType === 'BadManager') return BAD_MANAGER_COLOUR;
    if (unit.enemyType === 'TributeAct') return TRIBUTE_COLOUR;
    if (unit.enemyType === 'MusicPress') return MUSIC_PRESS_COLOUR;
    if (unit.enemyType === 'FilePirate') return PIRATE_COLOUR;
    return ENEMY_COLOUR;
  }
}
